import { Box, Stack, Typography } from '@mui/material';
import { LastActivityEntry, MessageForGroup } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ChannelTextField } from '@schooly/components/threads';
import { SimpleCard, Skeleton, ThreadIcon, TypographyWithOverflowHint } from '@schooly/style';
import { getRelativeTimeLabel } from '@schooly/utils/date';
import { getUserFirstName } from '@schooly/utils/user-helpers';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

type ThreadCardProps = {
  message: MessageForGroup;
  isUnread: boolean;
  lastActivity?: LastActivityEntry;
  isActivityLoading?: boolean;
  onClick: () => void;
};

export const ThreadCard: FC<ThreadCardProps> = ({
  message,
  isUnread,
  lastActivity,
  isActivityLoading,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  const { relationId } = useAuth();

  const dateLabel = useMemo(() => {
    const date = lastActivity ? new Date(lastActivity.last_reply_at) : undefined;

    if (!date) return '';
    return getRelativeTimeLabel(date, (d, v) => formatMessage(d, v));
  }, [lastActivity, formatMessage]);

  const senderName = useMemo(() => {
    if (!lastActivity) return null;

    const threadUser = lastActivity.last_reply_sender;
    return threadUser.relation_id === relationId
      ? formatMessage({ id: 'messages-You' })
      : `${getUserFirstName(threadUser)}`;
  }, [lastActivity?.last_reply_sender, relationId, formatMessage, lastActivity]);

  const subtitle =
    lastActivity?.last_reply_text && senderName
      ? `${senderName}: "${lastActivity.last_reply_text}"`
      : null;

  return (
    <SimpleCard
      onClick={onClick}
      actions={
        <Stack alignSelf="flex-end" alignItems="flex-end" mr={1} mt={1} gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              backgroundColor: 'success.main',
              borderRadius: '50%',
              mr: 0.5,
              visibility: isUnread ? 'visible' : 'hidden',
            }}
          />
          {isActivityLoading ? (
            <Skeleton variant="text" sx={{ width: 32, height: 14, borderRadius: '1px' }} />
          ) : (
            <Typography color="text.secondary" whiteSpace="nowrap">
              {dateLabel}
            </Typography>
          )}
        </Stack>
      }
      avatar={
        <Box
          aria-hidden
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'success.superLight',
            border: `1px solid ${theme.palette.common.green6}`,
            svg: { width: 20, height: 20, color: theme.palette.success.main },
          })}
        >
          <ThreadIcon />
        </Box>
      }
      sx={{
        borderBottom: 'none',
        position: 'relative',
        '.MuiCardActionArea-root': {
          height: '63px',
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '88%',
          height: '1px',
          backgroundColor: 'divider',
        },
      }}
    >
      <Stack gap={0.25} pl={1.25}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <TypographyWithOverflowHint maxWidth={350} variant="h3" color="common.grey2">
            {message.title}
          </TypographyWithOverflowHint>
        </Stack>
        {isActivityLoading ? (
          <Skeleton
            variant="text"
            sx={{ width: 80, height: 14, borderRadius: '1px', flexShrink: 0 }}
          />
        ) : (
          subtitle && (
            <TypographyWithOverflowHint maxWidth={350} variant="body2" color="common.grey3">
              {subtitle}
            </TypographyWithOverflowHint>
          )
        )}
      </Stack>
    </SimpleCard>
  );
};

export const ThreadWidgetChannelLoading: FC = () => {
  return (
    <Stack height="100%" justifyContent="space-between" px={2} pt={2}>
      <Stack gap={1.5} direction="row">
        <Skeleton variant="circular" width={44} height={44} sx={{ alignSelf: 'flex-start' }} />
        <Stack
          gap={0.5}
          pt={0.25}
          sx={{
            '& > .MuiStack-root': {
              backgroundColor: 'common.grey7',
              p: 1.5,
              height: '100%',
              wordBreak: 'break-word',
              position: 'relative',
              gap: 0.5,
            },
            '.MuiSkeleton-root': {
              backgroundColor: 'white',
            },
          }}
        >
          <Stack
            sx={(theme) => ({
              borderRadius: theme.spacing(2, 2, 2, 0),
              minWidth: '370px',
            })}
          >
            <Skeleton variant="rectangular" width="92%" height={12} />
            <Skeleton variant="rectangular" width="76%" height={12} />
          </Stack>
          <Stack
            sx={(theme) => ({
              borderRadius: theme.spacing(0, 2, 2, 2),
              width: '118px',
            })}
          >
            <Skeleton variant="rectangular" width="98%" height={12} />
            <Skeleton variant="rectangular" width="68%" height={12} />
          </Stack>
        </Stack>
      </Stack>

      <ChannelTextField isLoading />
    </Stack>
  );
};
